// The map's filter panel: a port of MapFilterSheet.swift over the shared `MapFilter` (domain/records.ts), with the
// iOS sections and wording. Differences for a desk: changes apply as you make them (the result count updates live)
// instead of an Apply bar, Owner and Stage come right after the quick filters (WEB_PLAN.md §5.7), and Tags (with a
// store count per tag) sits next to Store type, since imported store lists are told apart by their tags. The page
// keeps the filter in the URL query (`filterToQuery`), the format the Stores list shares.

import { useId, type ReactNode } from 'react';
import {
  PRIORITIES,
  PRIORITY_COLORS,
  PRIORITY_TITLES,
  RECORD_TYPES,
  RECORD_TYPE_PLURALS,
  USER_ROLE_TITLES,
  type Priority,
  type RecordType,
} from '@/contracts/enums';
import { orderedStages, type PipelinePayload, type TerritoryPayload, type UserPayload, type VisitResultPayload } from '@/contracts/models';
import { formatGrouped } from '@/domain/format';
import {
  FILTER_PRESETS,
  FILTER_PRESET_COLORS,
  FILTER_PRESET_TITLES,
  EMPTY_FILTER,
  RECORD_TYPE_PIN_COLORS,
  filterActiveCount,
  isPresetOn,
  togglePreset,
  type MapFilter,
} from '@/domain/records';
import { fullName, hasId, idSet } from '@/domain/util';
import { Button, Checkbox, Chip, ColorDot, Input, SectionLabel, Switch, cn } from '@/ui';
import { DAY_RANGE_PRESETS, dayRangePresetOn, setDayBound, toggleId, toggleValue, type DayBoundKey } from './filterEdits';
import type { TagOption } from './mapViewModel';

/** Tag chips are teal on iOS (`Theme.teal`). */
const TAG_COLOR = '#009E9E';

export interface TerritoryOption {
  territory: TerritoryPayload;
  /** Stores in it (`records(in:)`), shown under the name. */
  storeCount: number;
}

export interface MapFilterPanelProps {
  filter: MapFilter;
  onChange: (next: MapFilter) => void;
  /** Stores matching the current filter. */
  resultCount: number;
  /** `pickerUsers`: visible active people, by name (managers only see this section). */
  owners: readonly UserPayload[];
  showOwners: boolean;
  pipelines: readonly PipelinePayload[];
  /** In `order`. */
  visitResults: readonly VisitResultPayload[];
  territories: readonly TerritoryOption[];
  /** `tagOptions`: every tag on the visible stores with its store count. */
  tags: readonly TagOption[];
  className?: string;
}

export function MapFilterPanel({
  filter,
  onChange,
  resultCount,
  owners,
  showOwners,
  pipelines,
  visitResults,
  territories,
  tags,
  className,
}: MapFilterPanelProps) {
  const active = filterActiveCount(filter);
  const set = (patch: Partial<MapFilter>) => onChange({ ...filter, ...patch });
  // Lower-case id sets, built once per render rather than once per row.
  const ownerIds = idSet(filter.ownerIds);
  const pipelineIds = idSet(filter.pipelineIds);
  const stageIds = idSet(filter.stageIds);
  const resultIds = idSet(filter.visitResultIds);
  const territoryIds = idSet(filter.territoryIds);

  return (
    <div className={cn('flex flex-col', className)}>
      <div className="sticky top-0 z-[1] flex items-center gap-3 border-b border-line bg-surface px-4 py-3">
        <div className="min-w-0 flex-1" aria-live="polite">
          <p className="font-rounded text-xl leading-none font-bold text-fg tabular-nums">{formatGrouped(resultCount)}</p>
          <p className="mt-1 text-[10px] font-semibold tracking-[0.06em] text-fg-muted uppercase">
            {resultCount === 1 ? 'Result' : 'Results'}
            {active > 0 && ` · ${active} ${active === 1 ? 'filter' : 'filters'}`}
          </p>
        </div>
        <Button variant="secondary" size="sm" disabled={active === 0} onClick={() => onChange({ ...EMPTY_FILTER })}>
          Reset
        </Button>
      </div>

      <div className="flex flex-col gap-5 px-4 py-4">
        <Section title="Quick filters" footer="Shortcuts that set the criteria below. Select again to clear.">
          <ChipRow>
            {FILTER_PRESETS.map((p) => (
              <Chip
                key={p}
                color={FILTER_PRESET_COLORS[p]}
                selected={isPresetOn(filter, p, visitResults)}
                onClick={() => onChange(togglePreset(filter, p, visitResults))}
              >
                {FILTER_PRESET_TITLES[p]}
              </Chip>
            ))}
          </ChipRow>
        </Section>

        {showOwners && (
          <Section title="Owner">
            <CheckList>
              <CheckRow
                label="Unassigned"
                sub="No owner"
                color="#8C919E"
                checked={filter.includeUnassigned}
                onChange={(v) => set({ includeUnassigned: v })}
              />
              {owners.map((u) => (
                <CheckRow
                  key={u.id}
                  label={fullName(u) || 'Unnamed'}
                  sub={USER_ROLE_TITLES[u.role]}
                  color={u.colorHex}
                  checked={hasId(ownerIds, u.id)}
                  onChange={() => set({ ownerIds: toggleId(filter.ownerIds, u.id) })}
                />
              ))}
            </CheckList>
          </Section>
        )}

        <Section title="Stage">
          {pipelines.length === 0 ? (
            <Muted>No pipelines configured</Muted>
          ) : (
            <div className="flex flex-col gap-3">
              {pipelines.map((p) => {
                const pipelineOn = hasId(pipelineIds, p.id);
                return (
                  <div key={p.id} className="flex flex-col gap-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <SectionLabel>{p.name}</SectionLabel>
                      <Chip
                        size="xs"
                        color="#102A5E"
                        selected={pipelineOn}
                        title={`Only stores on the ${p.name} pipeline`}
                        onClick={() => set({ pipelineIds: toggleId(filter.pipelineIds, p.id) })}
                      >
                        Whole pipeline
                      </Chip>
                    </div>
                    <ChipRow>
                      {orderedStages(p).map((s) => (
                        <Chip
                          key={s.id}
                          color={s.colorHex}
                          selected={hasId(stageIds, s.id)}
                          onClick={() => set({ stageIds: toggleId(filter.stageIds, s.id) })}
                        >
                          {s.name}
                        </Chip>
                      ))}
                    </ChipRow>
                  </div>
                );
              })}
            </div>
          )}
        </Section>

        <Section title="Store type">
          <ChipRow>
            {RECORD_TYPES.map((t: RecordType) => (
              <Chip
                key={t}
                color={RECORD_TYPE_PIN_COLORS[t]}
                selected={filter.types.includes(t)}
                onClick={() => set({ types: toggleValue(filter.types, t) })}
              >
                {RECORD_TYPE_PLURALS[t]}
              </Chip>
            ))}
          </ChipRow>
        </Section>

        {tags.length > 0 && (
          <Section title="Tags" footer={tags.length > 1 ? 'Stores with any of the chosen tags.' : undefined}>
            <ChipRow>
              {tags.map(({ tag, count }) => (
                <Chip
                  key={tag}
                  color={TAG_COLOR}
                  title={`${tag}: ${formatGrouped(count)} ${count === 1 ? 'store' : 'stores'}`}
                  selected={filter.tags.includes(tag)}
                  onClick={() => set({ tags: toggleValue(filter.tags, tag) })}
                >
                  {tag}
                  <span className="ml-1.5 font-normal tabular-nums opacity-75">{formatGrouped(count)}</span>
                </Chip>
              ))}
            </ChipRow>
          </Section>
        )}

        <Section title="Last visit result">
          {visitResults.length === 0 ? (
            <Muted>No visit results configured</Muted>
          ) : (
            <ChipRow>
              {visitResults.map((r) => (
                <Chip
                  key={r.id}
                  color={r.colorHex}
                  selected={hasId(resultIds, r.id)}
                  onClick={() => set({ visitResultIds: toggleId(filter.visitResultIds, r.id) })}
                >
                  {r.name}
                </Chip>
              ))}
            </ChipRow>
          )}
        </Section>

        <Section title="Priority">
          <div className="grid grid-cols-5 gap-2">
            {PRIORITIES.map((p: Priority) => {
              const on = filter.priorities.includes(p);
              return (
                <button
                  key={p}
                  type="button"
                  aria-pressed={on}
                  aria-label={`${p} · ${PRIORITY_TITLES[p]}`}
                  title={PRIORITY_TITLES[p]}
                  onClick={() => set({ priorities: toggleValue(filter.priorities, p) })}
                  className={cn(
                    'h-8 rounded-full font-rounded text-sm font-bold transition-[filter,transform] active:scale-[0.97]',
                    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
                  )}
                  style={
                    on
                      ? { backgroundColor: PRIORITY_COLORS[p], color: p === 'C' ? '#102A5E' : '#FFFFFF' }
                      : { backgroundColor: `${PRIORITY_COLORS[p]}1F`, color: PRIORITY_COLORS[p], boxShadow: `inset 0 0 0 0.8px ${PRIORITY_COLORS[p]}4D` }
                  }
                >
                  {p}
                </button>
              );
            })}
          </div>
        </Section>

        <Section title="Territory">
          {territories.length === 0 ? (
            <Muted>No territories yet</Muted>
          ) : (
            <CheckList>
              {territories.map(({ territory: t, storeCount }) => (
                <CheckRow
                  key={t.id}
                  label={t.name.trim() === '' ? 'Untitled territory' : t.name}
                  sub={`${formatGrouped(storeCount)} ${storeCount === 1 ? 'store' : 'stores'}${t.isActive ? '' : ' · Inactive'}`}
                  color={t.colorHex}
                  checked={hasId(territoryIds, t.id)}
                  onChange={() => set({ territoryIds: toggleId(filter.territoryIds, t.id) })}
                />
              ))}
            </CheckList>
          )}
        </Section>

        <DaysRange
          title="Days since last visit"
          footer='Stores that were never visited are excluded when a range is set. Use "Never visited" below to find them.'
          filter={filter}
          minKey="daysSinceVisitMin"
          maxKey="daysSinceVisitMax"
          onChange={onChange}
        />
        <DaysRange
          title="Days since last activity"
          footer='Counts visits, calls, texts, emails, notes and tasks. Stores with no activity at all match an "at least" bound.'
          filter={filter}
          minKey="daysSinceActivityMin"
          maxKey="daysSinceActivityMax"
          onChange={onChange}
        />

        <Section title="More">
          <div className="flex flex-col gap-3">
            <Switch label="Only my stores" checked={filter.onlyMine} onCheckedChange={(v) => set({ onlyMine: v })} />
            <Switch label="Never visited" checked={filter.neverVisited} onCheckedChange={(v) => set({ neverVisited: v })} />
            <Switch
              label="Has a scheduled activity"
              checked={filter.hasScheduledActivity}
              onCheckedChange={(v) => set({ hasScheduledActivity: v })}
            />
            <Switch label="Follow-up due by today" checked={filter.followUpDue} onCheckedChange={(v) => set({ followUpDue: v })} />
          </div>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, footer, children }: { title: string; footer?: string; children: ReactNode }) {
  const id = useId();
  return (
    <section aria-labelledby={id} className="flex flex-col gap-2">
      <h3 id={id} className="text-[11px] font-bold tracking-[0.06em] text-fg-muted uppercase">
        {title}
      </h3>
      {children}
      {footer && <p className="text-xs text-fg-subtle">{footer}</p>}
    </section>
  );
}

function ChipRow({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap gap-1.5">{children}</div>;
}

function Muted({ children }: { children: ReactNode }) {
  return <p className="text-xs text-fg-muted">{children}</p>;
}

function CheckList({ children }: { children: ReactNode }) {
  return <div className="flex max-h-64 flex-col gap-0.5 overflow-y-auto rounded-control border border-line p-1 gt-scroll">{children}</div>;
}

/** iOS `checkRow`: color dot, name, caption, check mark. */
function CheckRow({
  label,
  sub,
  color,
  checked,
  onChange,
}: {
  label: string;
  sub: string;
  color: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <Checkbox
      checked={checked}
      onCheckedChange={(v) => onChange(v)}
      label={
        <span className="inline-flex min-w-0 items-center gap-1.5">
          <ColorDot color={color} size={10} />
          <span className="truncate">{label}</span>
        </span>
      }
      description={sub}
      className="rounded-[10px] px-2 py-1.5 hover:bg-surface-2"
    />
  );
}

function DaysRange({
  title,
  footer,
  filter,
  minKey,
  maxKey,
  onChange,
}: {
  title: string;
  footer: string;
  filter: MapFilter;
  minKey: DayBoundKey;
  maxKey: DayBoundKey;
  onChange: (next: MapFilter) => void;
}) {
  const lo = filter[minKey];
  const hi = filter[maxKey];
  return (
    <Section title={title} footer={footer}>
      <div className="flex flex-wrap gap-1.5" role="group" aria-label={`${title} ranges`}>
        {DAY_RANGE_PRESETS.map((p) => (
          <Chip
            key={p.id}
            color="#102A5E"
            selected={dayRangePresetOn(filter, minKey, maxKey, p)}
            onClick={() => onChange(setDayBound(setDayBound(filter, minKey, p.min), maxKey, p.max))}
          >
            {p.title}
          </Chip>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <DayInput label="At least" value={lo} onChange={(v) => onChange(setDayBound(filter, minKey, v))} />
        <DayInput label="At most" value={hi} onChange={(v) => onChange(setDayBound(filter, maxKey, v))} />
      </div>
    </Section>
  );
}

function DayInput({ label, value, onChange }: { label: string; value: number | undefined; onChange: (v: number | undefined) => void }) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-xs text-fg-muted">
        {label}
      </label>
      <Input
        id={id}
        type="number"
        inputMode="numeric"
        min={0}
        max={3650}
        size="sm"
        placeholder="Any"
        value={value === undefined ? '' : String(value)}
        onChange={(e) => {
          const raw = e.currentTarget.value.trim();
          if (raw === '') return onChange(undefined);
          const n = Math.floor(Number(raw));
          if (Number.isFinite(n)) onChange(Math.min(3650, Math.max(0, n)));
        }}
        trailing={<span className="text-xs text-fg-subtle">days</span>}
      />
    </div>
  );
}

